import {Component, OnInit} from '@angular/core';
import {HupSubscriptionsService} from '../../../data/services/hup-subscriptions.service';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslationService} from '../../../core/services/translation.service';
import {HupSubscription} from '../../../data/models/subscription.model';
import {ConfigService} from "../../../core/services/config.service";
import {NavigationCommand, NavigationService} from "../../../data/services/navigation.service";
import {BaseDataService} from "../../../core/services/base-data.service";
import {BaseData} from "../../../data/models/base-data.model";
import {PaymentService} from "../../../data/services/payment.service";
import {FormOfPayment} from "../../../data/models/formOfPayment.model";
import {LoginService} from "../../../core/components/login/login.service";
import {UserData} from "../../../data/models/user-data.model";
import {LandingPageService} from "../../../data/services/landing-page.service";

const VALID_BLOCK_KEYS = ['PAYMENT', 'CONTRACT_CURRENT', 'CONTRACT_FUTURE', 'ADR_DELIVERY', 'ADR_BILLING'] as const;

export type SubscriptionBlockKey = typeof VALID_BLOCK_KEYS[number];

@Component({
  selector: 'app-hup-subscription-detail',
  templateUrl: './hup-subscription-detail.component.html',
  styleUrls: ['./hup-subscription-detail.component.scss']
})
export class HupSubscriptionDetailComponent implements OnInit {
  sub: HupSubscription;
  showAdditionalSubscriptionInfos: boolean = true;
  showTerminationButton: boolean = true;
  currentDate = new Date();
  invoiceMethods: BaseData[] = [];
  paymentMethods: BaseData[] = [];
  showBackToOverviewButton: boolean;
  paymentMethodId: FormOfPayment;
  paymentMethodDesc: string = "";
  aggregateSubscription: boolean = false;
  showSequelSubscriptionInAggregateMode: boolean = false;
  userData: UserData;
  blockOrder: SubscriptionBlockKey[] = ['PAYMENT', 'CONTRACT_CURRENT', 'CONTRACT_FUTURE', 'ADR_DELIVERY', 'ADR_BILLING'];
  isMoveServiceActive = true;

  constructor(private hupSubscriptionsService: HupSubscriptionsService,
              private route: ActivatedRoute,
              public translationService: TranslationService,
              private configService: ConfigService,
              private router: Router,
              private navigationService: NavigationService,
              private baseData: BaseDataService,
              private paymentService: PaymentService,
              private loginService: LoginService,
              private landingPageService: LandingPageService
              ) {

    this.configService.loadConfig('showAdditionalSubscriptionInfos').subscribe(config => {
      if( config) {
        this.showAdditionalSubscriptionInfos = config.value === '1' || config.value === 'true';
      }
    });

    this.configService.loadConfig('subscription_show_termination_button').subscribe(config => {
      if( config && config.value) {
        this.showTerminationButton = config.value === '1' || config.value === 'true';
      }
    });

    this.configService.loadConfig('show.backToOverView.button').subscribe(config => {
      if( config && config.value) {
        this.showBackToOverviewButton = config.value === '1' || config.value === 'true';
      }
    });

    this.configService.loadConfig('subscription.aggregateSequelAndCombi').subscribe(config => {
      if( config && config.value) {
        this.aggregateSubscription = config.value === '1' || config.value === 'true';
      }
    });

    this.configService.loadConfig('subscription.showSequelInAggregateMode').subscribe(config => {
      if( config && config.value) {
        this.showSequelSubscriptionInAggregateMode = config.value === '1' || config.value === 'true';
      }
    });

    let confBlockOrder = this.configService.getConfig('subscription.blockOrder');
    if (!!confBlockOrder?.value) {
      this.blockOrder = confBlockOrder.value
        .split(',')
        .map(k => k.trim())
        .filter((key): key is SubscriptionBlockKey => VALID_BLOCK_KEYS.includes(key as SubscriptionBlockKey));
    }

    this.invoiceMethods = this.baseData.getBaseData('invoicemethods');
    this.paymentMethods = this.baseData.getBaseData('paymentmethods');

    this.landingPageService.isMoveServiceActive().subscribe(flag => {
      this.isMoveServiceActive = flag;
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.paymentMethodId = null;
      if (this.aggregateSubscription) {
        this.hupSubscriptionsService.getSubscriptionForDetail(params.get('webId'), params.get('backendId')).subscribe(sub => {
          this.sub = sub;
          this.handleSubscription();
        });
      } else {
        this.hupSubscriptionsService.getSubscription(params.get('webId'), params.get('backendId')).subscribe(sub => {
          this.sub = sub;
          this.handleSubscription();
        });
      }
    });
    this.loginService.getUserData().subscribe(userData => {
      if (userData) {
        this.userData = userData;
      }
    })
  }

  private handleSubscription() {
    this.paymentService.getPaymentMethodByCode(this.sub?.curPayment?.paymentType?.paymentMethod).subscribe(result => {
      this.paymentMethodId = result;
    });
    const payment = this.paymentMethods?.filter(method => method.key === this.sub?.curPayment?.paymentType?.paymentMethod)[0]?.description;
    const frequency = this.invoiceMethods?.filter(method => method.key === this.sub?.curPayment?.paymentType?.paymentFrequency)[0]?.description;
    this.paymentMethodDesc = payment ?? this.sub.curPayment.paymentType.paymentMethod;
    this.sub!.curPayment!.paymentType!.paymentFrequency = frequency ?? this.sub.curPayment.paymentType.paymentFrequency;
    if (this.sub.validDate.validUntil !== null && this.sub.validDate.validUntil < this.currentDate) {
      this.showTerminationButton = false;
    }
  }

  currentAndSequelSubscriptionsAreSimilar(){
    if (!(!!this.sub.combiSubscriptionList && !!this.sub.sequelSubscriptions)) {
      return false;
    }

    if (this.sub.combiSubscriptionList?.length !== this.sub.sequelSubscriptions?.length) {
      return false;
    }

    return this.sub.combiSubscriptionList?.every((currentSub, index) => {
      const sequelSub = this.sub.sequelSubscriptions[index];
      let subsAreSimilar = sequelSub &&
        currentSub.productCode === sequelSub.productCode &&
        currentSub.variantCode === sequelSub.variantCode &&
        currentSub.offer.price === sequelSub.offer.price;

      if (subsAreSimilar) {
        sequelSub.validDate.validFrom = currentSub.validDate.validFrom;
      }

      return subsAreSimilar;
    })
  }

  terminateSubscription() {
    this.router.navigate(['/terminate-subscription/' + this.sub.subId], {queryParamsHandling: "preserve"});
  }

  backToOverview(): void {
    this.navigationService.navigateTo(NavigationCommand.SUBSCRIPTION, null, true);
  }

  /**
   * Kann das Abonnement storniert werden?
   * Voraussetzungen für mögliche Stornierung:
   * 1. Keine Abokündigung liegt zur Überprüfung vor (= Keine Kündigungsanfrage liegt in der WebDB vor).
   * @param subscription - Abo, wessen Kündbarkeit überprüft werden soll.
   */
  isTerminatable(subscription: HupSubscription) {
    return !(subscription.webId > 0 && subscription.backendId > 0 && subscription.state === -1);
  }
}
